import { MultipleInsurancesForm } from "./multipleInsurancesForm";
import { StepperContent } from "@app/components/ui/layout";
import { MultipleInsurance } from "@app/components/ui/inputs/multipleInsurancesSelector/multipleInsurancesSelector";
import {
  PaymentType,
  SubSteps,
} from "@app/pages/private/simpleOrder/paymentMethod/paymentMethod";
import { StepperContext } from "@natera/stepper";
import React, { useContext, useEffect } from "react";
import { defineMessages, useIntl } from "react-intl";
import {
  CommercialStep,
  GovernmentStep,
} from "@app/components/simpleOrder/payment/steps";
import MultipleInsurancesSecondStep from "./multipleInsurancesSecondStep";
import {
  MultiplePaymentMethodType,
  PaymentMethodType,
  SimpleOrderStepperContext,
} from "@app/provider/simpleOrder/SimpleOrderStepperProvider";
import { Steps } from "@app/pages/private/simpleOrder";

const messages = defineMessages({
  multipleInsurancesStepHeaderTitle: {
    id: "multipleInsurancesStepHeaderTitle",
    defaultMessage: "Payment Method",
  },
  multipleInsurancesStepHeaderSubTitle: {
    id: "multipleInsurancesStepHeaderSubTitle",
    defaultMessage: "More than one insurance",
  },
  multipleInsurancesStepHeaderTextFirstType: {
    id: "multipleInsurancesStepHeaderTextFirstType",
    defaultMessage:
      "Select the <b>first type</b> of insurance that you would like to use to cover the cost of your test.",
  },
  multipleInsurancesFormFirstInsurancesTitle: {
    id: "multipleInsurancesFormFirstInsurancesTitle",
    defaultMessage: "First insurance type",
  },
});

type Props = {
  onConfirm: (data: unknown) => void;
};

const MultipleInsurancesFirstStep: React.FC<Props> = ({ onConfirm }) => {
  const intl = useIntl();
  const { goBack, resolve, getData, createStep, removeStep, goAt } =
    React.useContext(StepperContext);

  const stepData = getData(SubSteps.MULTIPLE_FIRST);

  const { getSubStepData, resolveSubStep } = useContext(
    SimpleOrderStepperContext
  );
  const subStepsData = getSubStepData(
    Steps.PAYMENT_METHOD
  ) as MultiplePaymentMethodType;

  const getSelectedInsurance = () => {
    const paymentMethod = subStepsData?.paymentMethodInfo?.first?.paymentMethod;
    if (paymentMethod === PaymentType.COMMERCIAL) {
      return MultipleInsurance.Commercial;
    } else if (paymentMethod === PaymentType.GOVERNMENT_ISSUED) {
      return MultipleInsurance.Government;
    }
    return stepData;
  };

  const [selectedInsurance, setSelectedInsurance] = React.useState<
    MultipleInsurance | undefined
  >(getSelectedInsurance());

  useEffect(() => {
    if (selectedInsurance) {
      handleSelectInsurance(selectedInsurance);
    }
  }, []);

  const resolveMultipleFirst = () => {
    createStep(SubSteps.MULTIPLE_SECOND, {
      children: <MultipleInsurancesSecondStep onConfirm={onConfirm} />,
      data: undefined,
      index: 5,
    });

    goAt(SubSteps.MULTIPLE_SECOND);
  };

  const addSteps = (insurance: MultipleInsurance) => {
    if (insurance === MultipleInsurance.Commercial) {
      createStep(SubSteps.SELECT_COMMERCIAL_COMPANY_FIRST, {
        children: <CommercialStep onConfirm={resolveMultipleFirst} />,
        index: 2,
      });
    } else {
      createStep(SubSteps.SELECT_GOVERNMENT_COMPANY_FIRST, {
        children: <GovernmentStep onConfirm={resolveMultipleFirst} />,
        index: 2,
      });
    }
  };

  const removeSteps = (insurance: MultipleInsurance | undefined) => {
    if (insurance === MultipleInsurance.Commercial) {
      removeStep(SubSteps.SELECT_COMMERCIAL_COMPANY_FIRST);
      removeStep(SubSteps.PROVIDE_COMMERCIAL_INSURANCE_FIRST);
      removeStep(SubSteps.ENTER_OTHER_COMMERCIAL_COMPANY_FIRST);
    }
    if (insurance === MultipleInsurance.Government) {
      removeStep(SubSteps.SELECT_GOVERNMENT_COMPANY_FIRST);
      removeStep(SubSteps.PROVIDE_GOVERNMENT_INSURANCE_FIRST);
      removeStep(SubSteps.ENTER_OTHER_GOVERNMENT_COMPANY_FIRST);
    }

    removeStep(SubSteps.MULTIPLE_SECOND);
  };

  const handleSelectInsurance = (insurance: MultipleInsurance) => {
    removeSteps(selectedInsurance);
    addSteps(insurance);

    setSelectedInsurance(insurance);
  };

  const handleSubmit = (insurance: string) => {
    const paymentMethod =
      insurance === MultipleInsurance.Commercial
        ? PaymentType.COMMERCIAL
        : PaymentType.GOVERNMENT_ISSUED;

    const paymentMethodInfoFirst: PaymentMethodType = { paymentMethod };

    if (subStepsData?.paymentMethodInfo?.first?.paymentMethod !== paymentMethod)
      paymentMethodInfoFirst.paymentMethodInfo = undefined;

    resolveSubStep({
      stepId: Steps.PAYMENT_METHOD,
      subStepData: {
        paymentMethodInfo: { first: { ...paymentMethodInfoFirst } },
      },
    });
    resolve(insurance);
  };

  const handleBack = () => {
    goBack();
  };

  return (
    <StepperContent
      title={intl.formatMessage(messages.multipleInsurancesStepHeaderTitle)}
      subTitle={intl.formatMessage(
        messages.multipleInsurancesStepHeaderSubTitle
      )}
      description={intl.formatMessage(
        messages.multipleInsurancesStepHeaderTextFirstType,
        { b: (...chunks) => <b>{chunks}</b> }
      )}
    >
      <MultipleInsurancesForm
        selectedInsurance={selectedInsurance}
        onSelect={handleSelectInsurance}
        onSubmit={handleSubmit}
        goBack={handleBack}
        label={intl.formatMessage(
          messages.multipleInsurancesFormFirstInsurancesTitle
        )}
      />
    </StepperContent>
  );
};

export default MultipleInsurancesFirstStep;
